import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, Smartphone, Bike, Calendar as CalendarIcon, Cloud, Apple, ExternalLink, Check, RefreshCw, Copy } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { SkeletonLoader, useToast } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";
import { formatError } from "@/lib/format-error";
import { FormInput, PrimaryButton, SecondaryButton } from "@/components/forms";
import { GarminConnectSection } from "@/components/integrations/GarminConnectSection";

/**
 * BW7 — `/integrations` lists every external data source Haus can connect
 * to. Each card shows current state from the `integration` table and a CTA
 * for connecting / re-connecting. Strava is the first OAuth provider; HAE is
 * a webhook-based pull (provisioned during BW6).
 *
 * Worker base URL is read from `VITE_HAUS_WORKER_URL` so dev / prod can
 * point at different deployments. The Strava "Connect" button hits
 * `/api/strava/oauth/start?user_id=…` on the worker, which redirects to
 * Strava and ultimately back to the worker callback that writes to
 * `integration`.
 */

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

type Integration = {
  id: string;
  provider: string;
  status: string;
  auth_state: string | null;
  connected_at: string | null;
  auth_expires_at: string | null;
};

type Provider = {
  id: string;
  label: string;
  description: string;
  Icon: typeof Activity;
  cta:
    | { kind: "oauth"; path: string; copy?: string }
    | { kind: "manual"; href?: string; copy?: string };
};

const PROVIDERS: Provider[] = [
  {
    id: "strava",
    label: "Strava",
    description:
      "Pulls completed activities every 15 minutes plus push webhook for instant sync.",
    Icon: Bike,
    cta: { kind: "oauth", path: "/api/strava/oauth/start", copy: "Connect Strava" },
  },
  {
    id: "garmin_healthkit",
    label: "Garmin via Health Auto Export",
    description:
      "Sleep, HRV, RHR, steps, active energy. Configured by pointing the iOS Health Auto Export app at the Haus webhook.",
    Icon: Smartphone,
    cta: {
      kind: "manual",
      copy: "Setup guide",
      href: "/docs/integrations/garmin",
    },
  },
  // BW20.2 PR-G: Garmin Connect (direct) is rendered by GarminConnectSection
  // below the provider list — it has its own status / sync-now / disconnect /
  // reset flow rather than the standard ProviderRow CTA.
  {
    id: "google_calendar",
    label: "Google Calendar",
    description:
      "Inbound — your Google Calendar events appear on the Haus day view alongside workouts and project deadlines.",
    Icon: CalendarIcon,
    cta: { kind: "oauth", path: "/api/calendar/google/oauth/start", copy: "Connect Google" },
  },
];

type CredentialProvider = {
  id: "icloud_calendar";
  label: string;
  description: string;
  Icon: typeof Activity;
  helpUrl: string;
  helpLabel: string;
  fields: Array<{ key: string; label: string; placeholder?: string; type?: string }>;
  connectPath: string;
  syncPath: string;
};

const CRED_PROVIDERS: CredentialProvider[] = [
  {
    id: "icloud_calendar",
    label: "iCloud Calendar",
    description:
      "Inbound — pulls your iCloud Calendar events every 30 min via CalDAV. Requires an Apple-ID + 16-char app-specific password.",
    Icon: Cloud,
    helpUrl: "https://support.apple.com/en-us/HT204397",
    helpLabel: "How to make an app-specific password",
    fields: [
      { key: "apple_id", label: "Apple ID", placeholder: "you@icloud.com", type: "email" },
      { key: "app_password", label: "App-specific password", placeholder: "xxxx-xxxx-xxxx-xxxx" },
    ],
    connectPath: "/api/calendar/icloud/connect",
    syncPath: "/api/calendar/icloud/sync",
  },
];

export function Integrations() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["integrations", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<Integration[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("integration")
        .select("id, provider, status, auth_state, connected_at, auth_expires_at")
        .eq("user_id", userId)
        .order("connected_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const byProvider = new Map((q.data ?? []).map((i) => [i.provider, i]));

  return (
    <article aria-labelledby="integrations-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Settings
      </p>
      <h1
        id="integrations-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        Integrations
      </h1>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={2} height={88} />
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: "var(--space-3)",
          }}
        >
          {PROVIDERS.map((p) => {
            const integ = byProvider.get(p.id);
            return (
              <li key={p.id}>
                <ProviderRow provider={p} integration={integ} userId={userId} />
              </li>
            );
          })}
          {CRED_PROVIDERS.map((p) => {
            const integ = byProvider.get(p.id);
            return (
              <li key={p.id}>
                <CredentialProviderRow provider={p} integration={integ} userId={userId} />
              </li>
            );
          })}
          <li>
            <CronometerInfoCard />
          </li>
          <li>
            <GarminConnectSection />
          </li>
        </ul>
      )}

      <SubscribeBlock />

      <p
        style={{
          marginTop: "var(--space-6)",
          color: "var(--fg-3)",
          fontSize: "var(--type-body-s)",
          lineHeight: 1.5,
        }}
      >
        More providers (Withings, Apple Health bidirectional) land in later
        build weeks. See{" "}
        <Link to="/" style={{ color: "var(--accent)" }}>
          spec section 2.1
        </Link>{" "}
        for the full integration roadmap.
      </p>
    </article>
  );
}

function SubscribeBlock() {
  const toast = useToast();
  const [token, setToken] = useState<string | null>(null);
  const mint = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("mint_calendar_token", { p_scope: "all" });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (t) => {
      setToken(t);
      toast.show("Subscription link minted.");
    },
    onError: (e) => toast.show(`Couldn't mint token: ${formatError(e)}`),
  });

  const url = token
    ? `${WORKER_URL}/api/calendar/ical/${token}.ics`
    : null;

  return (
    <section
      style={{
        marginTop: "var(--space-8)",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <header
        style={{
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: "var(--space-2)",
        }}
      >
        Outbound · Subscribe to Haus
      </header>
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)", lineHeight: 1.55 }}>
        Add a read-only Haus feed to Apple / Google / Outlook calendar. Includes
        every project deadline (with 7-day + 1-day reminders) and planned workouts.
      </p>
      {url ? (
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", marginTop: "var(--space-3)" }}>
          <code
            style={{
              flex: 1,
              padding: "8px 10px",
              background: "var(--bg-3)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-chip)",
              fontSize: "var(--type-meta)",
              color: "var(--fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {url}
          </code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(url);
              toast.show("Copied subscription URL.");
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "8px 12px",
              background: "transparent",
              color: "var(--fg-2)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-chip)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
            }}
          >
            <Copy size={14} strokeWidth={1.5} /> Copy
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => mint.mutate()}
          disabled={mint.isPending}
          style={{
            marginTop: "var(--space-3)",
            padding: "8px 14px",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          {mint.isPending ? "Generating…" : "Generate subscription URL"}
        </button>
      )}
    </section>
  );
}

/**
 * Cronometer's only supported "API" is community-reverse-engineered and
 * Gold-only — and Cronometer doesn't actually publish one. Both BW10
 * paths broke after one sync. Replaced with documentation of the two
 * paths that do work today: photo screenshots and the CSV export.
 */
function CronometerInfoCard() {
  return (
    <article
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr",
        gap: "var(--space-4)",
        alignItems: "start",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: "var(--radius-card)",
          background: "var(--bg-3)",
          color: "var(--fg-2)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Apple size={22} strokeWidth={1.5} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            color: "var(--fg)",
            fontSize: "var(--type-body)",
            fontWeight: 500,
          }}
        >
          Cronometer
          <span
            style={{
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            · No API
          </span>
        </div>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)", lineHeight: 1.5 }}>
          Cronometer doesn't expose a public API for individual diaries. Two paths work today:
        </p>
        <div
          style={{
            display: "grid",
            gap: "var(--space-2)",
            gridTemplateColumns: "1fr",
            marginTop: "var(--space-2)",
          }}
        >
          <PathTile
            title="Daily — photo upload"
            body="Screenshot Cronometer's daily summary, upload via Today → Quick log → Photo. Claude extracts items + verifies macros."
            cta="Quick log →"
            to="/"
          />
          <PathTile
            title="Bulk — CSV export"
            body="Cronometer.com → Settings → Account → Export Data → CSV. Drop the file at /import/cronometer."
            cta="Open import →"
            to="/import/cronometer"
          />
        </div>
      </div>
    </article>
  );
}

function PathTile({
  title,
  body,
  cta,
  to,
}: {
  title: string;
  body: string;
  cta: string;
  to: string;
}) {
  return (
    <div
      style={{
        padding: "var(--space-3)",
        background: "var(--bg-3)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ fontSize: "var(--type-body-s)", fontWeight: 500, color: "var(--fg)" }}>{title}</div>
      <p style={{ margin: 0, fontSize: "var(--type-meta)", color: "var(--fg-3)", lineHeight: 1.5 }}>
        {body}
      </p>
      <Link
        to={to}
        style={{
          color: "var(--accent)",
          fontSize: "var(--type-meta)",
          textDecoration: "none",
          marginTop: 2,
        }}
      >
        {cta}
      </Link>
    </div>
  );
}

function ProviderRow({
  provider,
  integration,
  userId,
}: {
  provider: (typeof PROVIDERS)[number];
  integration: Integration | undefined;
  userId: string | undefined;
}) {
  const connected = integration?.auth_state === "connected";
  const needsReconnect = integration?.auth_state === "needs_reconnect";
  const Icon = provider.Icon;

  return (
    <article
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr max-content",
        gap: "var(--space-4)",
        alignItems: "center",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: `1px solid ${connected ? "var(--accent-dim)" : needsReconnect ? "var(--brick)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: "var(--radius-card)",
          background: connected ? "var(--accent-wash)" : needsReconnect ? "color-mix(in oklab, var(--brick) 12%, transparent)" : "var(--bg-3)",
          color: connected ? "var(--accent)" : needsReconnect ? "var(--brick)" : "var(--fg-2)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            color: "var(--fg)",
            fontSize: "var(--type-body)",
            fontWeight: 500,
          }}
        >
          {provider.label}
          {connected && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                color: "var(--accent)",
                fontSize: "var(--type-meta)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              <Check size={12} strokeWidth={2} /> Connected
            </span>
          )}
        </div>
        <p
          style={{
            margin: 0,
            color: "var(--fg-3)",
            fontSize: "var(--type-body-s)",
            lineHeight: 1.5,
          }}
        >
          {provider.description}
        </p>
        {connected && integration?.connected_at && (
          <p
            className="mono"
            style={{
              margin: 0,
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
            }}
          >
            since {formatShortDate(integration.connected_at)}
          </p>
        )}
        {needsReconnect && (
          <p
            style={{
              margin: 0,
              color: "var(--brick)",
              fontSize: "var(--type-body-s)",
              lineHeight: 1.5,
            }}
          >
            Reconnect required. The access token was rejected by {provider.label}. Click Re-connect to reauthorize.
          </p>
        )}
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
        {connected && (provider.id === "strava" || provider.id === "google_calendar") && userId && (
          <SyncNowButton provider={provider.id} userId={userId} />
        )}
        <ProviderCta
          provider={provider}
          connected={connected}
          userId={userId}
        />
      </div>
    </article>
  );
}

function CredentialProviderRow({
  provider,
  integration,
  userId,
}: {
  provider: CredentialProvider;
  integration: Integration | undefined;
  userId: string | undefined;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(provider.fields.map((f) => [f.key, ""])),
  );
  const connected = integration?.auth_state === "connected";
  const Icon = provider.Icon;

  const connect = useMutation({
    mutationFn: async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) throw new Error("Not signed in");
      const resp = await fetch(`${WORKER_URL}${provider.connectPath}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${s.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(vals),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 200)}`);
      }
    },
    onSuccess: () => {
      toast.show(`${provider.label} connected.`);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["integrations", userId] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const sync = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const url = `${WORKER_URL}${provider.syncPath}?user_id=${encodeURIComponent(userId)}`;
      const resp = await fetch(url, { method: "POST" });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 200)}`);
      }
      return await resp.json();
    },
    onSuccess: (r: { imported?: number }) => {
      toast.show(`Synced — ${r.imported ?? 0} imported.`);
      void qc.invalidateQueries({ queryKey: ["integrations", userId] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  return (
    <article
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr max-content",
        gap: "var(--space-4)",
        alignItems: "center",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: `1px solid ${connected ? "var(--accent-dim)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: "var(--radius-card)",
          background: connected ? "var(--accent-wash)" : "var(--bg-3)",
          color: connected ? "var(--accent)" : "var(--fg-2)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            color: "var(--fg)",
            fontSize: "var(--type-body)",
            fontWeight: 500,
          }}
        >
          {provider.label}
          {connected && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                color: "var(--accent)",
                fontSize: "var(--type-meta)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              <Check size={12} strokeWidth={2} /> Connected
            </span>
          )}
        </div>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)", lineHeight: 1.5 }}>
          {provider.description}
        </p>
        {open && (
          <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
            {provider.fields.map((f) => (
              <FormInput
                key={f.key}
                label={f.label}
                type={f.type ?? "text"}
                value={vals[f.key] ?? ""}
                onChange={(v) => setVals((s) => ({ ...s, [f.key]: v }))}
                placeholder={f.placeholder}
              />
            ))}
            <a
              href={provider.helpUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: "var(--accent)", fontSize: "var(--type-meta)" }}
            >
              {provider.helpLabel} ↗
            </a>
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
              <SecondaryButton onClick={() => setOpen(false)} disabled={connect.isPending}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={() => connect.mutate()} disabled={connect.isPending}>
                {connect.isPending ? "Connecting…" : "Connect"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
        {connected && userId && (
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "8px 12px",
              background: "transparent",
              color: "var(--fg-2)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              cursor: sync.isPending ? "not-allowed" : "pointer",
            }}
          >
            <RefreshCw size={14} strokeWidth={1.5} />
            {sync.isPending ? "Syncing…" : "Sync"}
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            padding: "8px 14px",
            background: connected ? "transparent" : "var(--accent)",
            color: connected ? "var(--fg-2)" : "#0a0b0d",
            border: `1px solid ${connected ? "var(--hair-2)" : "transparent"}`,
            borderRadius: "var(--radius-card)",
            fontSize: "var(--type-body-s)",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          {connected ? "Re-connect" : "Connect"}
        </button>
      </div>
    </article>
  );
}

function SyncNowButton({ provider, userId }: { provider: string; userId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const path = provider === "strava" ? "/api/strava/sync" : "/api/calendar/google/sync";
  const sync = useMutation({
    mutationFn: async () => {
      const url = `${WORKER_URL}${path}?user_id=${encodeURIComponent(userId)}`;
      const resp = await fetch(url, { method: "POST" });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 120)}`);
      }
      return (await resp.json()) as {
        ok: boolean;
        imported?: number;
        fetched?: number;
        skipped?: number;
        unmapped?: number;
        unmapped_types?: string[];
        hint?: string;
      };
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["integrations", userId] });
      const parts: string[] = [`${r.imported ?? 0} imported`];
      if ((r.fetched ?? 0) > 0) parts.push(`${r.fetched} fetched`);
      if ((r.skipped ?? 0) > 0) parts.push(`${r.skipped} skipped`);
      if ((r.unmapped ?? 0) > 0) {
        const types = r.unmapped_types?.join(", ") ?? "";
        parts.push(`${r.unmapped} unmapped${types ? ` (${types})` : ""}`);
      }
      toast.show(`Synced: ${parts.join(", ")}.`);
      if (r.hint) {
        setTimeout(() => toast.show(r.hint!), 1000);
      }
    },
    onError: (e) => toast.show(formatError(e)),
  });
  return (
    <button
      type="button"
      onClick={() => sync.mutate()}
      disabled={sync.isPending}
      title="Sync now"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "8px 12px",
        background: "transparent",
        color: "var(--fg-2)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        fontSize: "var(--type-body-s)",
        cursor: sync.isPending ? "not-allowed" : "pointer",
      }}
    >
      <RefreshCw size={14} strokeWidth={1.5} />
      {sync.isPending ? "Syncing…" : "Sync"}
    </button>
  );
}

function ProviderCta({
  provider,
  connected,
  userId,
}: {
  provider: (typeof PROVIDERS)[number];
  connected: boolean;
  userId: string | undefined;
}) {
  if (provider.cta.kind === "oauth") {
    if (!userId) return null;
    const href = `${WORKER_URL}${provider.cta.path}?user_id=${encodeURIComponent(userId)}`;
    return (
      <a
        href={href}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          background: connected ? "transparent" : "var(--accent)",
          color: connected ? "var(--fg-2)" : "#0a0b0d",
          border: `1px solid ${connected ? "var(--hair-2)" : "transparent"}`,
          borderRadius: "var(--radius-card)",
          fontSize: "var(--type-body-s)",
          fontWeight: 500,
          whiteSpace: "nowrap",
          textDecoration: "none",
        }}
      >
        {connected ? "Re-connect" : (provider.cta.copy ?? "Connect")}
        <ExternalLink size={14} strokeWidth={1.5} />
      </a>
    );
  }
  const href = provider.cta.href ?? "#";
  const internal = href.startsWith("/");
  const ctaStyles: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    background: "transparent",
    color: "var(--fg-2)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    fontSize: "var(--type-body-s)",
    fontWeight: 500,
    whiteSpace: "nowrap",
    textDecoration: "none",
  };
  if (internal) {
    return (
      <Link to={href} style={ctaStyles}>
        {provider.cta.copy ?? "Open"}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      style={ctaStyles}
    >
      {provider.cta.copy ?? "Open"}
      <ExternalLink size={14} strokeWidth={1.5} />
    </a>
  );
}
